import type { PropertyValues } from 'lit';
import { CSSResult, html } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';

import { AvatarComponentMixin } from '../../utils/mixins/AvatarComponentMixin';
import { IconNameMixin } from '../../utils/mixins/IconNameMixin';
import { ROLE } from '../../utils/roles';
import { AVATAR_SIZE, DEFAULTS } from '../avatar/avatar.constants';
import type { AvatarSize } from '../avatar/avatar.types';
import { DEFAULTS as BUTTON_DEFAULTS } from '../button/button.constants';
import Buttonsimple from '../buttonsimple/buttonsimple.component';

import styles from './avatarbutton.styles';

/**
 * The `mdc-avatarbutton` component is an interactive, clickable version of `mdc-avatar`.
 * It wraps the avatar component in a button, making it focusable and actionable.
 *
 * This component extends `buttonsimple` and includes all avatar display features (photo, initials, icon, counter, presence).
 * Use CSS parts to customize the avatar's appearance inside the button.
 *
 * ## Accessibility
 * - Always provide an `aria-label` attribute to describe the button's purpose
 *
 * @dependency mdc-avatar
 *
 * @event click - (React: onClick) Dispatched when the avatar button is clicked.
 * @event keydown - (React: onKeyDown) Dispatched when a key is pressed on the avatar button.
 * @event keyup - (React: onKeyUp) Dispatched when a key is released on the avatar button.
 * @event focus - (React: onFocus) Dispatched when the avatar button receives focus.
 *
 * @tagname mdc-avatarbutton
 *
 * @cssproperty --mdc-avatarbutton-overlay-background-color-rest - Background color of the overlay in rest state.
 * @cssproperty --mdc-avatarbutton-overlay-background-color-hover - Background color of the overlay in hover state.
 * @cssproperty --mdc-avatarbutton-overlay-background-color-active - Background color of the overlay in active state.
 * @cssproperty --mdc-avatarbutton-default-background-color - Default background color of the avatar.
 * @cssproperty --mdc-avatarbutton-default-foreground-color - Default foreground color of the avatar.
 * @cssproperty --mdc-avatarbutton-loading-indicator-background-color - Background color of the loading indicator.
 * @cssproperty --mdc-avatarbutton-loading-indicator-foreground-color - Foreground color of the loading indicator.
 * @cssproperty --mdc-avatarbutton-loading-overlay-background-color - Background color of the loading overlay.
 *
 * @csspart overlay - The overlay part of the avatar button.
 * @csspart content - The main content of the avatar.
 * @csspart photo - The photo part of the avatar.
 * @csspart presence - The presence indicator part of the avatar.
 * @csspart loading-wrapper - The wrapper for the loading indicator of the avatar.
 * @csspart loader - The loading indicator part of the avatar.
 */
class AvatarButton extends AvatarComponentMixin(IconNameMixin(Buttonsimple)) {
  /**
   * Aria-label attribute to be set for accessibility
   */
  @property({ type: String, attribute: 'aria-label' })
  override ariaLabel: string | null = null;

  override connectedCallback(): void {
    super.connectedCallback();

    this.active = undefined as unknown as boolean;
    this.disabled = undefined as unknown as boolean;
    this.softDisabled = undefined as unknown as boolean;
    this.role = ROLE.BUTTON;
    this.type = BUTTON_DEFAULTS.TYPE;
  }

  override update(changedProperties: PropertyValues): void {
    super.update(changedProperties);
    if (changedProperties.has('size')) {
      this.setSize(this.size);
    }
  }

  private setSize(size: AvatarSize) {
    this.setAttribute('size', Object.values(AVATAR_SIZE).includes(size) ? `${size}` : DEFAULTS.SIZE.toString());
  }

  public override render() {
    return html`
      <div part="overlay" aria-hidden="true"></div>
      <mdc-avatar
        exportparts="content, photo, presence, loading-wrapper, loader"
        ?is-typing="${this.isTyping}"
        counter="${ifDefined(this.counter)}"
        icon-name="${ifDefined(this.iconName)}"
        initials="${ifDefined(this.initials)}"
        presence="${ifDefined(this.presence)}"
        size="${ifDefined(this.size)}"
        src="${ifDefined(this.src)}"
      ></mdc-avatar>
    `;
  }

  public static override styles: Array<CSSResult> = [...styles];
}

export default AvatarButton;
